import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    AcademicCapIcon, ClockIcon, CheckCircleIcon, XCircleIcon,
    ArrowRightCircleIcon, SparklesIcon, ClipboardDocumentCheckIcon,
    StarIcon, XMarkIcon, LockClosedIcon, ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom'; 
import { db, auth } from '../../firebase'; 
import { collection, getDocs, query, orderBy, doc, getDoc, setDoc } from 'firebase/firestore';

// [REF: GLASS_CONFIRM_MODAL] - مكون التأكيد الزجاجي المنبثق بالوسط
const GlassConfirm = ({ isOpen, onConfirm, onCancel }) => (
    <AnimatePresence>
        {isOpen && (
            <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[10000] flex items-center justify-center p-6 backdrop-blur-md bg-black/40"
            >
                <motion.div 
                    initial={{ scale: 0.9, y: 20, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.9, y: 20, opacity: 0 }}
                    className="ios-glass max-w-sm w-full bg-white/90 dark:bg-[#1C1C1E]/80 p-8 rounded-[40px] shadow-2xl border border-white/20 text-center space-y-6"
                >
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto text-red-600">
                        <ExclamationTriangleIcon className="w-10 h-10" />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-900 dark:text-white font-cairo">تنبيه الخروج!</h3>
                        <p className="text-sm font-bold text-gray-500 dark:text-gray-400 mt-2 leading-relaxed font-cairo">
                            هل أنت متأكد من الخروج؟ إذا خرجت الآن لن يتم حفظ درجتك في هذا الاختبار.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={onConfirm} className="flex-1 py-4 bg-red-500 text-white rounded-2xl font-black shadow-lg shadow-red-500/20 active:scale-95 transition-all font-cairo">خروج</button>
                        <button onClick={onCancel} className="flex-1 py-4 bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-white rounded-2xl font-black active:scale-95 transition-all font-cairo">استمرار</button>
                    </div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
);

const StudentQuizzes = () => {
    const navigate = useNavigate();
    
    const [quizzes, setQuizzes] = useState([]); 
    const [studentResults, setStudentResults] = useState([]); 
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [activeQuiz, setActiveQuiz] = useState(null);
    const [quizStarted, setQuizStarted] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [showExitConfirm, setShowExitConfirm] = useState(false); // حالة النافذة الزجاجية

    // [SECTION: FETCH_DATA] - جلب البيانات والتحقق من الاشتراك
    const fetchFreshData = useCallback(async () => {
        if (!auth.currentUser) return;
        const uid = auth.currentUser.uid;

        try {
            const userDoc = await getDoc(doc(db, "users", uid));
            const uData = userDoc.data();
            setUserData(uData);

            const resultsSnapshot = await getDocs(collection(db, "users", uid, "quizResults"));
            const resultsData = resultsSnapshot.docs.map(doc => ({
                ...doc.data(),
                percentage: Number(doc.data().percentage) || 0
            }));
            setStudentResults(resultsData);

            if (uData?.activeCourse) {
                const q = query(collection(db, "quizzes"), orderBy("createdAt", "desc"));
                const querySnapshot = await getDocs(q);
                const allQuizzes = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const filtered = allQuizzes.filter(quiz => 
                    uData?.activeCourse === "كورسين (كامل)" || 
                    quiz.courseType === uData?.activeCourse
                );
                setQuizzes(filtered);
            } else {
                setQuizzes([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFreshData();
    }, [fetchFreshData]);

    const totalQuizzesAvailable = quizzes.length;
    const totalCompleted = studentResults.length;
    const sumOfPercentages = studentResults.reduce((sum, item) => sum + item.percentage, 0);
    const averageScore = totalQuizzesAvailable > 0 ? Math.round(sumOfPercentages / totalQuizzesAvailable) : 0;
    const trophies = studentResults.filter(r => r.percentage >= 90).length;

    useEffect(() => {
        let timer;
        if (quizStarted && timeLeft > 0 && !showResult && !showExitConfirm) {
            timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
        } else if (quizStarted && timeLeft === 0) {
            finishQuiz(score); 
        }
        return () => clearInterval(timer);
    }, [quizStarted, timeLeft, showResult, score, showExitConfirm]);

    const startQuizHandler = (quiz) => {
        setActiveQuiz(quiz);
        setTimeLeft(quiz.questions.length * 60); 
        setQuizStarted(true);
        setShowResult(false);
        setCurrentQuestion(0);
        setScore(0);
        setSelectedAnswer(null);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const handleAnswer = (index) => {
        if (selectedAnswer !== null) return;
        setSelectedAnswer(index);
        
        const correctIdx = Number(activeQuiz.questions[currentQuestion].correct);
        let newScore = score; 

        if (Number(index) === correctIdx) {
            newScore = score + 1;
            setScore(newScore);
        }

        setTimeout(() => {
            if (currentQuestion < activeQuiz.questions.length - 1) {
                setCurrentQuestion(prev => prev + 1);
                setSelectedAnswer(null);
            } else {
                finishQuiz(newScore);
            }
        }, 800);
    };

    const finishQuiz = async (passedScore) => {
        const finalScore = passedScore !== undefined ? passedScore : score;
        const finalPercentage = Math.round((finalScore / activeQuiz.questions.length) * 100);
        setShowResult(true);

        try {
            const uid = auth.currentUser.uid;
            const resultRef = doc(collection(db, "users", uid, "quizResults"), activeQuiz.id);
            const resultData = {
                quizId: activeQuiz.id,
                quizTitle: activeQuiz.title,
                score: Number(finalScore),
                totalQuestions: Number(activeQuiz.questions.length),
                percentage: Number(finalPercentage),
                date: new Date().toISOString()
            };
            await setDoc(resultRef, resultData);
            await fetchFreshData();
        } catch (e) { console.error("Error saving result:", e); }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-transparent">
            <div className="w-10 h-10 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    const percentage = activeQuiz ? Math.round((score / activeQuiz.questions.length) * 100) : 0;

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-700 pb-10 text-right dark:text-white" dir="rtl">
            
            {/* 🛡️ نافذة التأكيد الزجاجية الملكية */}
            <GlassConfirm 
                isOpen={showExitConfirm} 
                onConfirm={() => { setShowExitConfirm(false); setQuizStarted(false); }} 
                onCancel={() => setShowExitConfirm(false)} 
            />

            <AnimatePresence mode="wait">
                {!quizStarted ? (
                    <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-8 px-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gradient-to-br from-[#007AFF] to-[#00C7FF] p-8 rounded-[40px] text-white shadow-xl shadow-blue-500/20 relative overflow-hidden group border border-white/20">
                                <StarIcon className="w-32 h-32 absolute -bottom-8 -left-8 opacity-10 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-bold opacity-80 leading-none mb-2 font-cairo">معدلك التراكمي الحقيقي</h3>
                                <p className="text-7xl font-black font-cairo">{userData?.activeCourse ? `${averageScore}%` : "--"}</p>
                                <div className="mt-6 flex items-center gap-2 text-sm font-black bg-white/20 w-fit px-4 py-2 rounded-2xl font-cairo">
                                    <AcademicCapIcon className="w-5 h-5" /> 
                                    {userData?.activeCourse ? (averageScore >= 85 ? "أنت من النخبة!" : averageScore >= 50 ? "استمر، أنت في الطريق الصحيح!" : "شد حيلك لإتمام المنهج!") : "بانتظار تفعيل الكورس"}
                                </div>
                            </div>
                            
                            <div className="ios-glass p-8 rounded-[40px] border border-white/40 dark:border-white/10 shadow-lg bg-white/20 dark:bg-white/5 flex flex-col justify-center text-center">
                                <h3 className="text-gray-400 font-black text-sm uppercase mb-4 font-cairo">ملخص النشاط</h3>
                                <div className="flex justify-between items-center px-4 font-cairo">
                                    <div>
                                        <p className="text-3xl font-black text-gray-800 dark:text-white leading-none mb-1">{totalCompleted}</p>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">منجز</p>
                                    </div>
                                    <div className="w-[1px] h-10 bg-gray-200 dark:bg-white/10"></div>
                                    <div>
                                        <p className="text-3xl font-black text-[#FF9500] leading-none mb-1">{Math.max(0, totalQuizzesAvailable - totalCompleted)}</p>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">متبقي</p>
                                    </div>
                                    <div className="w-[1px] h-10 bg-gray-200 dark:bg-white/10"></div>
                                    <div>
                                        <p className="text-3xl font-black text-[#34C759] leading-none mb-1">{trophies}</p>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">أوسمة</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h2 className="text-xl font-black text-gray-800 dark:text-white px-4 flex items-center gap-2 font-cairo">
                                <ClipboardDocumentCheckIcon className="w-6 h-6 text-[#007AFF]" /> جدول الاختبارات
                            </h2>
                            <div className="grid grid-cols-1 gap-4">
                                {!userData?.activeCourse ? (
                                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-16 text-center ios-glass rounded-[40px] border-2 border-dashed border-gray-300 dark:border-white/10 flex flex-col items-center justify-center space-y-4 bg-white/10">
                                        <div className="w-20 h-20 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mb-2 shadow-inner"><LockClosedIcon className="w-10 h-10 text-gray-400" /></div>
                                        <h3 className="text-2xl font-black text-gray-800 dark:text-white font-cairo">قسم الاختبارات مغلق</h3>
                                        <p className="text-gray-500 max-w-sm px-6 font-bold leading-relaxed font-cairo">يرجى تفعيل كورس الأحياء أولاً لتتمكن من خوض الاختبارات الوزارية والمراجعات الشاملة.</p>
                                        <button onClick={() => navigate('/student/activate')} className="mt-4 px-10 py-4 bg-[#007AFF] text-white rounded-[20px] font-black shadow-lg shadow-blue-500/20 active:scale-95 transition-all font-cairo">تفعيل الاشتراك الآن</button>
                                    </motion.div>
                                ) : quizzes.length > 0 ? (
                                    quizzes.map((quiz, index) => {
                                        const result = studentResults.find(r => r.quizId === quiz.id);
                                        const isDone = !!result;
                                        return (
                                            <motion.div key={quiz.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className={`ios-glass p-5 rounded-[30px] border-white/50 dark:border-white/10 bg-white/20 dark:bg-white/5 flex flex-col md:flex-row items-center justify-between gap-4 hover:shadow-lg transition-all ${isDone ? 'bg-white/40' : ''}`} >
                                                <div className="flex items-center gap-5 w-full md:w-auto text-right font-cairo">
                                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner ${isDone ? 'bg-emerald-100 text-green-600' : 'bg-blue-100 dark:bg-blue-500/10 text-[#007AFF]'}`}>{isDone ? <CheckCircleIcon className="w-7 h-7" /> : <AcademicCapIcon className="w-7 h-7" />}</div>
                                                    <div>
                                                        <h4 className="font-black text-gray-800 dark:text-white text-sm leading-tight">{quiz.title}</h4>
                                                        <p className="text-[10px] font-bold text-gray-400 mt-1">{quiz.chapter} • {quiz.questions.length} سؤال {isDone && `• درجتك: ${result.percentage}%`}</p>
                                                    </div>
                                                </div>
                                                <button onClick={() => startQuizHandler(quiz)} className={`px-8 py-3 rounded-2xl font-black text-xs shadow-lg active:scale-95 flex items-center gap-2 transition-all font-cairo ${isDone ? 'bg-white border border-gray-100 text-gray-500' : 'bg-[#007AFF] text-white shadow-blue-500/20 hover:scale-105'}`}>{isDone ? "إعادة الاختبار" : "ابدأ الاختبار"} <ArrowRightCircleIcon className="w-4 h-4" /></button>
                                            </motion.div>
                                        );
                                    })
                                ) : (
                                    <div className="py-20 text-center ios-glass rounded-[40px] text-gray-400 font-bold font-cairo">لا توجد اختبارات مرفوعة حالياً لهذا الكورس.</div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                ) : !showResult ? (
                    <motion.div key="exam" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-3xl mx-auto space-y-6 pt-10 px-4">
                        <div className="flex justify-between items-center">
                            <div className="ios-glass px-6 py-3 rounded-2xl flex items-center gap-3 font-black text-[#007AFF] shadow-lg border-white/20 bg-white/20 dark:bg-black/20 font-cairo"><ClockIcon className="w-6 h-6 animate-pulse" /> {formatTime(timeLeft)}</div>
                            <button onClick={() => setShowExitConfirm(true)} className="p-3 ios-glass rounded-2xl text-red-500 border-white/20 shadow-md bg-white/20 dark:bg-black/20"><XMarkIcon className="w-6 h-6"/></button>
                        </div>
                        <div className="ios-glass p-10 rounded-[50px] border-white/40 dark:border-white/10 shadow-2xl relative overflow-hidden bg-white/40 dark:bg-black/20 font-cairo">
                            <div className="absolute top-0 left-0 h-2 bg-[#007AFF] transition-all duration-500" style={{ width: `${((currentQuestion + 1) / activeQuiz.questions.length) * 100}%` }}></div>
                            <h3 className="text-2xl font-black text-gray-800 dark:text-white mb-10 leading-relaxed text-right">{activeQuiz.questions[currentQuestion].text}</h3>
                            <div className="grid grid-cols-1 gap-4">
                                {activeQuiz.questions[currentQuestion].options.map((opt, i) => (
                                    <button key={i} onClick={() => handleAnswer(i)} className={`p-6 rounded-[25px] text-right font-bold transition-all border-2 text-lg flex justify-between items-center font-cairo ${selectedAnswer === i ? (Number(i) === Number(activeQuiz.questions[currentQuestion].correct) ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-500 text-emerald-700" : "bg-red-50 dark:bg-red-500/10 border-red-500 text-red-700") : "bg-white/50 dark:bg-white/5 border-white/20 dark:border-white/10 hover:border-[#007AFF]"}`}>
                                        <span>{opt}</span>
                                        {selectedAnswer === i && (Number(i) === Number(activeQuiz.questions[currentQuestion].correct) ? <CheckCircleIcon className="w-7 h-7 text-emerald-500"/> : <XCircleIcon className="w-7 h-7 text-red-500"/>)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div key="result" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl mx-auto ios-glass p-12 rounded-[60px] text-center space-y-8 shadow-2xl mt-10 dark:border-white/10 bg-white/20 dark:bg-black/20 font-cairo">
                        <div className="relative inline-block">
                            <svg className="w-48 h-48 transform -rotate-90">
                                <circle cx="96" cy="96" r="80" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-gray-100 dark:text-white/5" />
                                <circle cx="96" cy="96" r="80" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={502} strokeDashoffset={502 - (502 * score) / activeQuiz.questions.length} className={`${percentage >= 50 ? 'text-emerald-500' : 'text-red-500'} transition-all duration-1000`} strokeLinecap="round" />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-5xl font-black text-gray-800 dark:text-white leading-none">{percentage}%</span></div>
                        </div>
                        <div>
                            <h2 className="text-4xl font-black text-gray-900 dark:text-white">{percentage >= 90 ? "أسطورة الأحياء! 🔥" : percentage >= 50 ? "كفو يابطل! 🎉" : "لا بأس، حاول مجدداً! 💪"}</h2>
                            <p className="font-bold text-gray-500 dark:text-gray-400 mt-3 text-lg px-4 leading-relaxed">لقد أجبت على {score} من أصل {activeQuiz.questions.length} بشكل صحيح.</p>
                        </div>
                        <div className="flex gap-4">
                            <button onClick={() => { setQuizStarted(false); setActiveQuiz(null); }} className="flex-1 py-5 bg-gray-900 dark:bg-white dark:text-black text-white rounded-[25px] font-black hover:scale-105 transition-all shadow-lg font-cairo">العودة للجدول</button>
                            <button onClick={() => startQuizHandler(activeQuiz)} className="flex-1 py-5 bg-emerald-500 text-white rounded-[25px] font-black shadow-lg shadow-emerald-500/20 hover:scale-105 transition-all font-cairo">إعادة المحاولة</button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default StudentQuizzes;